export namespace DataIntegrity {
  export type Severity = "error" | "warn";
  export type DedupeStrategy = "none" | "rule-key-message";

  export type RuleIssue<TItem, TPatch = undefined> = {
    severity: Severity;
    message: string;
    relatedItems?: readonly TItem[];
    suggestedPatch?: TPatch;
  };

  export type DatasetRuleIssue<TItem, TPatch = undefined> = RuleIssue<TItem, TPatch> & {
    item: TItem;
  };

  export type Issue<_TItem, TRuleId extends string = string, TPatch = undefined> = {
    ruleId: TRuleId;
    severity: Severity;
    key: string;
    message: string;
    relatedKeys?: string[];
    suggestedPatch?: TPatch;
  };

  export type Report<TItem, TRuleId extends string = string, TPatch = undefined> = {
    issues: Issue<TItem, TRuleId, TPatch>[];
    errorCount: number;
    warningCount: number;
    byRule: Record<TRuleId, number>;
  };

  export type RowRule<TItem, TRuleId extends string = string, TPatch = undefined> = {
    id: TRuleId;
    check: (args: { item: TItem }) => RuleIssue<TItem, TPatch>[];
  };

  export type DatasetRule<TItem, TRuleId extends string = string, TPatch = undefined> = {
    id: TRuleId;
    check: (args: { items: readonly TItem[] }) => DatasetRuleIssue<TItem, TPatch>[];
  };

  export type RuleOverride<TRule extends { id: string }> =
    | { action: "off" }
    | { action: "replace"; rule: TRule }
    | { action: "transform"; transform: (rule: TRule) => TRule };

  export type RuleOverridesById<TRule extends { id: string }> = Partial<
    Record<TRule["id"], RuleOverride<TRule>>
  >;

  export type RuleSet<TItem, TRuleId extends string = string, TPatch = undefined> = {
    rowRules: readonly RowRule<TItem, TRuleId, TPatch>[];
    datasetRules: readonly DatasetRule<TItem, TRuleId, TPatch>[];
    override: (overrides: {
      row?: RuleOverridesById<RowRule<TItem, TRuleId, TPatch>>;
      dataset?: RuleOverridesById<DatasetRule<TItem, TRuleId, TPatch>>;
    }) => RuleSet<TItem, TRuleId, TPatch>;
    add: <TAddedRuleId extends string = never, TAddedPatch = never>(rules: {
      rowRules?: readonly RowRule<TItem, TAddedRuleId, TAddedPatch>[];
      datasetRules?: readonly DatasetRule<TItem, TAddedRuleId, TAddedPatch>[];
    }) => RuleSet<TItem, TRuleId | TAddedRuleId, TPatch | TAddedPatch>;
  };

  export type RunOptions<TItem, TRuleId extends string = string, TPatch = undefined> = {
    items: readonly TItem[];
    getKey: (item: TItem) => string;
    ruleSet: RuleSet<TItem, TRuleId, TPatch>;
    dedupe?: DedupeStrategy;
    ignored?: readonly IgnoreEntry<TRuleId>[];
  };

  export type IgnoreEntry<TRuleId extends string = string> = {
    key: string;
    ruleId: TRuleId;
  };

  export function applyRuleOverrides<TRule extends { id: string }>(
    rules: readonly TRule[],
    overrides?: RuleOverridesById<TRule>,
  ): TRule[] {
    if (!overrides) {
      return [...rules];
    }

    const out: TRule[] = [];
    for (const rule of rules) {
      const override = overrides[rule.id as TRule["id"]];
      if (!override) {
        out.push(rule);
        continue;
      }

      if (override.action === "off") {
        continue;
      }

      if (override.action === "replace") {
        out.push(override.rule);
        continue;
      }

      out.push(override.transform(rule));
    }

    return out;
  }

  function makeRuleSet<TItem, TRuleId extends string, TPatch>(
    rowRules: readonly RowRule<TItem, TRuleId, TPatch>[],
    datasetRules: readonly DatasetRule<TItem, TRuleId, TPatch>[],
  ): RuleSet<TItem, TRuleId, TPatch> {
    return {
      rowRules,
      datasetRules,
      override: (overrides) => {
        const nextRowRules = applyRuleOverrides(rowRules, overrides.row);
        const nextDatasetRules = applyRuleOverrides(datasetRules, overrides.dataset);
        return makeRuleSet(nextRowRules, nextDatasetRules);
      },
      add: <TAddedRuleId extends string = never, TAddedPatch = never>(rules: {
        rowRules?: readonly RowRule<TItem, TAddedRuleId, TAddedPatch>[];
        datasetRules?: readonly DatasetRule<TItem, TAddedRuleId, TAddedPatch>[];
      }) => {
        const nextRowRules = [
          ...rowRules,
          ...(rules.rowRules ?? []),
        ] as unknown as readonly RowRule<TItem, TRuleId | TAddedRuleId, TPatch | TAddedPatch>[];
        const nextDatasetRules = [
          ...datasetRules,
          ...(rules.datasetRules ?? []),
        ] as unknown as readonly DatasetRule<TItem, TRuleId | TAddedRuleId, TPatch | TAddedPatch>[];
        return makeRuleSet<TItem, TRuleId | TAddedRuleId, TPatch | TAddedPatch>(
          nextRowRules,
          nextDatasetRules,
        );
      },
    };
  }

  export function createRuleSet<TItem, TRuleId extends string = never, TPatch = undefined>(input?: {
    rowRules?: readonly RowRule<TItem, TRuleId, TPatch>[];
    datasetRules?: readonly DatasetRule<TItem, TRuleId, TPatch>[];
  }): RuleSet<TItem, TRuleId, TPatch> {
    return makeRuleSet(input?.rowRules ?? [], input?.datasetRules ?? []);
  }

  export function runChecks<TItem, TRuleId extends string = string, TPatch = undefined>(
    options: RunOptions<TItem, TRuleId, TPatch>,
  ): Report<TItem, TRuleId, TPatch> {
    const allIssues: Issue<TItem, TRuleId, TPatch>[] = [];

    for (const rule of options.ruleSet.rowRules) {
      for (const item of options.items) {
        const issues = rule.check({
          item,
        });
        const key = options.getKey(item);
        allIssues.push(
          ...issues.map((issue) => ({
            ruleId: rule.id,
            severity: issue.severity,
            key,
            message: issue.message,
            relatedKeys: issue.relatedItems?.map((relatedItem) => options.getKey(relatedItem)),
            suggestedPatch: issue.suggestedPatch,
          })),
        );
      }
    }

    for (const rule of options.ruleSet.datasetRules) {
      const issues = rule.check({
        items: options.items,
      });
      allIssues.push(
        ...issues.map((issue) => ({
          ruleId: rule.id,
          severity: issue.severity,
          key: options.getKey(issue.item),
          message: issue.message,
          relatedKeys: issue.relatedItems?.map((relatedItem) => options.getKey(relatedItem)),
          suggestedPatch: issue.suggestedPatch,
        })),
      );
    }

    const deduped = dedupeIssues(allIssues, options.dedupe ?? "rule-key-message");
    const ignoredKeys = new Set(
      (options.ignored ?? []).map((entry) => `${entry.key}|${entry.ruleId}`),
    );
    const issues =
      ignoredKeys.size === 0
        ? deduped
        : deduped.filter((issue) => !ignoredKeys.has(`${issue.key}|${issue.ruleId}`));

    let errorCount = 0;
    let warningCount = 0;
    const byRule = new Map<TRuleId, number>();

    for (const issue of issues) {
      if (issue.severity === "error") {
        errorCount++;
      } else {
        warningCount++;
      }
      byRule.set(issue.ruleId, (byRule.get(issue.ruleId) ?? 0) + 1);
    }

    return {
      issues,
      errorCount,
      warningCount,
      byRule: Object.fromEntries(byRule.entries()) as Record<TRuleId, number>,
    };
  }

  export function dedupeIssues<TItem, TRuleId extends string = string, TPatch = undefined>(
    issues: readonly Issue<TItem, TRuleId, TPatch>[],
    strategy: DedupeStrategy = "rule-key-message",
  ): Issue<TItem, TRuleId, TPatch>[] {
    if (strategy === "none") {
      return [...issues];
    }

    const out: Issue<TItem, TRuleId, TPatch>[] = [];
    const seen = new Set<string>();
    for (const issue of issues) {
      const dedupeKey = `${issue.ruleId}|${issue.key}|${issue.message}`;
      if (seen.has(dedupeKey)) {
        continue;
      }
      seen.add(dedupeKey);
      out.push(issue);
    }
    return out;
  }
}
